import { RequestResultValue } from "../../common/api/requestResult";
import { HttpService } from "../../common/api/httpService";
import {
    AdCampaign,
    AdCampaignListResult,
    AdCampaignStatus,
    CreateAdCampaignRequest,
    UpdateAdCampaignRequest,
} from "../../models/adCampaign";
import { AdminPromoCodeUsagesRequest, AdminPromoCodeUsagesResult } from "../../models/promoCode";

/** Результат запроса с текстом ошибки сервера (например, «Промокод уже используется»). */
export class AdCampaignApiResult<T> extends RequestResultValue<T> {
    public error?: string;

    constructor(res: boolean, v?: T, error?: string) {
        super(res, v);
        this.error = error;
    }
}

/** Текст ошибки из тела ответа API (ErrorInfo.Message). */
function readError(body: unknown): string | undefined {
    if (!body || typeof body !== "object") {
        return undefined;
    }
    const b = body as { Message?: string; message?: string; error?: { description?: string }; title?: string };
    return b.Message ?? b.message ?? b.error?.description ?? b.title;
}

/** Список кампаний (admin/AdCampaigns/list). */
export async function adCampaignsListApi(
    page: number,
    count: number,
    status?: AdCampaignStatus | null,
    search?: string
): Promise<RequestResultValue<AdCampaignListResult>> {
    const result = await HttpService.getInstance().adminFetch("AdCampaigns/list", "POST", {
        page,
        count,
        status: status ?? null,
        search: search || null,
    });
    if (result.result) {
        return new RequestResultValue(true, result.value as AdCampaignListResult);
    }
    return new RequestResultValue(false);
}

/** Карточка кампании по id. */
export async function adCampaignGetApi(id: number): Promise<RequestResultValue<AdCampaign>> {
    const result = await HttpService.getInstance().adminFetch(`AdCampaigns/${id}`, "GET");
    if (result.result) {
        return new RequestResultValue(true, result.value as AdCampaign);
    }
    return new RequestResultValue(false);
}

/** Создать кампанию; в ответе приходят Code и TrackingUrl. */
export async function adCampaignCreateApi(
    body: CreateAdCampaignRequest
): Promise<AdCampaignApiResult<AdCampaign>> {
    const result = await HttpService.getInstance().adminFetch("AdCampaigns", "POST", body);
    if (result.result) {
        return new AdCampaignApiResult(true, result.value as AdCampaign);
    }
    return new AdCampaignApiResult<AdCampaign>(false, undefined, readError(result.value));
}

/** Частичное обновление кампании. */
export async function adCampaignUpdateApi(body: UpdateAdCampaignRequest): Promise<AdCampaignApiResult<void>> {
    const result = await HttpService.getInstance().adminFetch("AdCampaigns", "PUT", body);
    return result.result
        ? new AdCampaignApiResult<void>(true)
        : new AdCampaignApiResult<void>(false, undefined, readError(result.value));
}

/** Использования промокодов (admin/AdCampaigns/promo-codes/list) с фильтрами. */
export async function adCampaignPromoCodeUsagesApi(
    request: AdminPromoCodeUsagesRequest
): Promise<AdCampaignApiResult<AdminPromoCodeUsagesResult>> {
    const result = await HttpService.getInstance().adminFetch("AdCampaigns/promo-codes/list", "POST", request);
    if (result.result) {
        return new AdCampaignApiResult(true, result.value as AdminPromoCodeUsagesResult);
    }
    return new AdCampaignApiResult<AdminPromoCodeUsagesResult>(false, undefined, readError(result.value));
}
